import type { IMenuDraft } from '@/interfaces/IMenuImport';
import { isNewDish, isUpdateDraft } from './live-update';

/**
 * Suggestion chips of «Pedir cambios» (plan §4.6): up to 3, the first that
 * apply in this order; a tap sends the chip's text as the message. In an
 * update session only new dishes can get descriptions, and the menu keeps
 * its category order (no «Ordenar las categorías»).
 */

export type TChatChip = 'shorten' | 'writeMissing' | 'raise' | 'reorder';

export const CHAT_CHIP_KEYS: Record<TChatChip, string> = {
  shorten: 'menuImport.chat.suggestShorten',
  writeMissing: 'menuImport.chat.suggestWriteMissing',
  raise: 'menuImport.chat.suggestRaise',
  reorder: 'menuImport.chat.suggestReorder',
};

export const MAX_CHAT_CHIPS = 3;
/** Descriptions longer than this are worth shortening. */
export const LONG_DESCRIPTION_CHARS = 120;
/** A chip about dishes or categories needs at least this many of them. */
const MIN_MATCHES = 3;

export function suggestionChips(draft: IMenuDraft): TChatChip[] {
  const update = isUpdateDraft(draft);
  const items = Object.values(draft.items).filter(
    (item) => !update || isNewDish(item)
  );
  const longDescriptions = items.filter(
    (item) => (item.description ?? '').length > LONG_DESCRIPTION_CHARS
  ).length;
  const missingDescriptions = items.filter(
    (item) => !(item.description ?? '').trim()
  ).length;

  const candidates: [TChatChip, boolean][] = [
    ['shorten', longDescriptions >= MIN_MATCHES],
    ['writeMissing', missingDescriptions >= MIN_MATCHES],
    ['raise', true],
    ['reorder', !update && draft.categoryOrder.length >= MIN_MATCHES],
  ];
  return candidates
    .filter(([, applies]) => applies)
    .map(([chip]) => chip)
    .slice(0, MAX_CHAT_CHIPS);
}
